<script setup lang="ts">
import { onMounted, ref, computed } from 'vue';
import { useLocale } from '@/plugins/translation';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

const props = defineProps<{
  title: string;
  index: string;
  icon: string;
}>();

const emit = defineEmits<{
  (e: 'next-section', sectionId: string): void;
  (e: 'intersect', isIntersecting: boolean, entries: IntersectionObserverEntry[]): void;
}>();

const { t, l } = useLocale();

const intersectionOptions = { threshold: [0, 0.5, 1.0] };
const sectionId = computed(() => `section${props.index.replace('.', '-')}`);
const isLastSection = ref(false);
onMounted(() => {
  isLastSection.value = !document.querySelector(`#${sectionId.value} + section`);
});
</script>
<template>
  <section
    :id="sectionId"
    v-intersect="{
      handler: (isIntersecting: boolean, entries: IntersectionObserverEntry[]) =>
        emit('intersect', isIntersecting, entries),
      options: intersectionOptions,
    }"
    :data-index="index"
    :class="[{ 'pt-16': index !== '1.1' }, 'pt-sm-2']"
  >
    <h2 class="h3 text-primary my-5 pt-4 pt-sm-0">
      <FontAwesomeIcon :icon="icon" size="sm" />
      {{ title }}
    </h2>
    <slot name="default"></slot>
    <v-btn
      v-if="!isLastSection"
      class="text-primary scroll-next-btn"
      icon
      elevation="0"
      @click="emit('next-section', `#${sectionId}`)"
      :aria-label="t(l.area_guide.label_go_to_next_section)"
    >
      <FontAwesomeIcon icon="fa-solid fa-chevrons-down" size="2xl"></FontAwesomeIcon>
    </v-btn>
  </section>
</template>
<style scoped lang="scss">
@use '@/foundations/theme/settings';
@use 'sass:map';

h3 {
  font-size: 1.875rem;
}

section {
  position: relative;
  min-height: calc(100vh - 64px);
  margin-bottom: 6em;
}

.scroll-next-btn {
  display: block;
  margin: 0 -14px 0 auto;
  width: auto;
  height: auto;
  padding: 5px;
  background-color: transparent !important;

  @media #{map.get(settings.$display-breakpoints, 'md-and-up')} {
    margin: 0 30px 0 auto;
    padding: 10px;
    position: absolute;
    left: calc(min(50vw, 750px));
  }
}
</style>
